import {
  type DocumentNode,
  type FieldNode,
  type FragmentDefinitionNode,
  type InlineFragmentNode,
  type SelectionSetNode,
  type SelectionNode,
  visit,
} from 'graphql';
import isNil from 'lodash/isNil';

export type UniqueFragmentsTransformerProps = {
  onError?: (message: string) => void;
  onTransform?: (message: string) => void;
};

export class UniqueFragmentsTransformer {
  onError: UniqueFragmentsTransformerProps['onError'];
  onTransform: UniqueFragmentsTransformerProps['onTransform'];

  constructor({ onError, onTransform }: UniqueFragmentsTransformerProps = {}) {
    this.onError = onError;
    this.onTransform = onTransform;
  }

  transform(document: DocumentNode): DocumentNode {
    const fragments = new Map<string, FragmentDefinitionNode>();

    return visit(document, {
      FragmentDefinition: {
        enter: (node) => {
          const previousFragment = fragments.get(node.name.value);
          if (previousFragment) {
            if (!isSameSelection(previousFragment.selectionSet, node.selectionSet)) {
              this.onError?.(`Found conflicting definitions for fragment ${node.name.value}.`);
            }
            this.onTransform?.(`Removing redefinition of fragment ${node.name.value}.`);
            return null;
          }

          fragments.set(node.name.value, node);
          return;
        },
      },
    });
  }
}

const isInlineFragmentNode = (object: SelectionNode): object is InlineFragmentNode => {
  return object.kind === 'InlineFragment';
};

const isSameSelection = (selection1: SelectionSetNode, selection2: SelectionSetNode) => {
  if (isNil(selection1) || isNil(selection2)) {
    return selection1 === selection2;
  }

  if (selection1.selections.length !== selection2.selections.length) {
    return false;
  }

  const selectionsMatch = selection1.selections.every((selection1Field, index): boolean => {
    const selection2Field = selection2.selections[index];
    if (selection1Field.kind !== selection2Field.kind) {
      return false;
    }

    if (isInlineFragmentNode(selection1Field) && isInlineFragmentNode(selection2Field)) {
      return isSameSelection(selection1Field.selectionSet, selection2Field.selectionSet);
    }

    return (
      (selection1Field as FieldNode).name?.value === (selection2Field as FieldNode).name?.value
    );
  });

  return selectionsMatch;
};
